import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Building2, 
  Plus,
  Search,
  Edit2,
  Trash2,
  ExternalLink
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";
import SemiTruck from "@/components/icons/SemiTruck";
import MechanicTools from "@/components/icons/MechanicTools";
import ClassicSemiTruck from "@/components/icons/ClassicSemiTruck";
import SemiTrailer from "@/components/icons/SemiTrailer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get icon component based on icon type
const getIconComponent = (iconType) => {
  if (iconType === 'tools') return MechanicTools;
  if (iconType === 'truck') return SemiTruck;
  if (iconType === 'classic-truck') return ClassicSemiTruck;
  if (iconType === 'semi-trailer') return SemiTrailer;
  return SemiTruck; // default
};

export default function AdminBusinesses() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    address: "",
    phone: "",
    email: "",
    zelle_email: ""
  });

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/admin/businesses`);
      setBusinesses(response.data);
    } catch (error) {
      toast.error("Failed to fetch businesses");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Generate slug from name if not provided
    const slug = formData.slug || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    
    try {
      if (editingBusiness) {
        await axios.put(`${API}/admin/businesses/${editingBusiness.id}`, {
          ...formData,
          slug
        });
        toast.success("Business updated successfully");
      } else {
        await axios.post(`${API}/admin/businesses`, {
          ...formData,
          slug
        });
        toast.success("Business created successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save business");
    }
  };

  const handleEdit = (business) => {
    setEditingBusiness(business);
    setFormData({
      name: business.name,
      slug: business.slug,
      address: business.address || "",
      phone: business.phone || "",
      email: business.email || "",
      zelle_email: business.zelle_email || ""
    });
    setIsDialogOpen(true);
  };

  const handleToggleActive = async (business) => {
    try {
      await axios.put(`${API}/admin/businesses/${business.id}`, {
        is_active: !business.is_active
      });
      toast.success(business.is_active ? "Business deactivated" : "Business activated");
      fetchBusinesses();
    } catch (error) {
      toast.error("Failed to update business status");
    }
  };

  const handleDeleteClick = (business) => {
    setBusinessToDelete(business);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!businessToDelete) return;
    
    try {
      // Permanently delete (we'll add this endpoint)
      await axios.delete(`${API}/admin/businesses/${businessToDelete.id}/permanent`);
      toast.success("Business permanently deleted");
      fetchBusinesses();
    } catch (error) {
      // Fallback to deactivate if permanent delete not available
      await axios.delete(`${API}/admin/businesses/${businessToDelete.id}`);
      toast.success("Business deactivated");
      fetchBusinesses();
    }
    setDeleteDialogOpen(false);
    setBusinessToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      address: "",
      phone: "",
      email: "",
      zelle_email: ""
    });
    setEditingBusiness(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const filteredBusinesses = businesses.filter(b =>
    b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-businesses">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Businesses
          </h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Manage all businesses on the platform
          </p>
        </div>
        <Button onClick={openCreateDialog} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="create-business-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Business
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
        <Input
          placeholder="Search businesses..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`pl-10 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
          data-testid="search-businesses"
        />
      </div>

      {/* Businesses List */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-0">
          {filteredBusinesses.length === 0 ? (
            <div className={`text-center py-12 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              {searchQuery ? "No businesses match your search" : "No businesses yet. Create your first one!"}
            </div>
          ) : (
            <div className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-200'}`}>
              {filteredBusinesses.map((business) => {
                const IconComponent = getIconComponent(business.icon);
                
                return (
                  <div key={business.id} className={`p-4 flex items-center justify-between ${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'} transition-colors`}>
                    {/* Clickable business info - goes to dashboard */}
                    <Link 
                      to={`/${business.slug}`} 
                      className="flex items-center gap-4 flex-1 cursor-pointer"
                    >
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-purple-100'}`}>
                        <IconComponent className={`w-6 h-6 ${isDark ? 'text-slate-400' : 'text-purple-600'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'} hover:text-purple-600 transition-colors`}>
                            {business.name}
                          </p>
                          {business.is_active ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 border-red-200">Inactive</Badge>
                          )}
                        </div>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          /{business.slug} • {business.phone || 'No phone'}
                        </p>
                        <p className={`text-xs mt-1 text-purple-500`}>
                          Click to view dashboard →
                        </p>
                      </div>
                    </Link>
                    
                    {/* Action buttons - separate from clickable area */}
                    <div className="flex items-center gap-3 ml-4">
                      {/* Active/Inactive Toggle */}
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {business.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <Switch
                          checked={business.is_active}
                          onCheckedChange={() => handleToggleActive(business)}
                          data-testid={`toggle-business-${business.slug}`}
                        />
                      </div>
                      {/* View Dashboard */}
                      <Link to={`/${business.slug}`}>
                        <Button variant="ghost" size="sm" className="text-purple-600">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </Link>
                      
                      {/* Edit */}
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(business)} data-testid={`edit-business-${business.slug}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      
                      {/* Delete */}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 hover:text-red-600 hover:bg-red-50" 
                        onClick={() => handleDeleteClick(business)}
                        data-testid={`delete-business-${business.slug}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-800'}>
              {editingBusiness ? "Edit Business" : "Create New Business"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Business Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Webster Truck Repair"
                required
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                data-testid="business-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>URL Slug</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="e.g., webster (auto-generated from name)"
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                data-testid="business-slug-input"
              />
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                URL will be: /{formData.slug || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'your-business'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 555-5555"
                  className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@business.com"
                  className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, State 12345"
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Zelle Email</Label>
              <Input
                type="email"
                value={formData.zelle_email}
                onChange={(e) => setFormData({ ...formData, zelle_email: e.target.value })}
                placeholder="payments@business.com"
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className={isDark ? 'border-slate-600' : 'border-slate-200'}>
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-business-btn">
                {editingBusiness ? "Save Changes" : "Create Business"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>
              Delete Business?
            </AlertDialogTitle>
            <AlertDialogDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Are you sure you want to permanently delete <strong>{businessToDelete?.name}</strong>? 
              This action cannot be undone. All data associated with this business will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDark ? 'border-slate-600' : 'border-slate-200'}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
